import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, ExternalLink, ShieldCheck } from "lucide-react";
import { sharedApi } from "@/lib/api";

/**
 * AFFILIATE-236: standalone "Check any car's history" widget for the
 * `/c/cars` landing page. Uses the PUBLIC endpoint
 * `GET /api/classifieds/carjam-link?plate=X` → `{plate, url}` and opens
 * the returned URL in a new tab.
 *
 * Zero cost per use (no CarJam API call) — this links to CarJam's own
 * free search page with our partner code so report purchases pay our 20%
 * share. Referrer discipline enforced: `rel="noopener"` on the pop-open.
 */
export default function CarHistoryCheckCard() {
  const [plate, setPlate] = useState("");

  const [fallbackUrl, setFallbackUrl] = useState("");

  const mut = useMutation({
    mutationFn: async () => {
      const clean = plate.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
      if (!clean) {
        const err = new Error("Enter a valid NZ plate.");
        err._client = true;
        throw err;
      }
      const { data } = await sharedApi.get("/classifieds/carjam-link", {
        params: { plate: clean },
      });
      return data;
    },
    onSuccess: (data) => {
      if (!data?.url) {
        toast.error("Couldn't build a CarJam link right now.");
        return;
      }
      // Referrer required for CarJam affiliate attribution (20% share).
      // `noopener` intentionally set so window.opener is null on the new
      // tab. Browsers return `null` from window.open under `noopener` even
      // on success, so we CANNOT infer popup-blocked from a null return.
      // We optimistically fire the open, always stash the fallback URL so
      // a manual click is available if the browser blocked the popup, and
      // trust the user to notice if the tab didn't appear.
      window.open(data.url, "_blank", "noopener");
      setFallbackUrl(data.url);
    },
    onError: (err) => {
      const msg =
        err?._client
          ? err.message
          : err?.response?.data?.detail ||
            err?.message ||
            "Couldn't build a CarJam link right now.";
      toast.error(typeof msg === "string" ? msg : "Invalid plate.");
    },
  });

  return (
    <div
      data-testid="car-history-check-card"
      className="rounded-3xl bg-gradient-to-br from-white to-[color:var(--brand-blue-wash)]/60 border border-[color:var(--brand-blue-wash)] p-5 md:p-6"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-white text-[color:var(--brand-blue)] grid place-items-center shrink-0 shadow-sm">
          <ShieldCheck className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg md:text-xl font-bold text-slate-900">
            Check any car&apos;s history — enter a plate
          </div>
          <p className="text-xs md:text-sm text-slate-600 mt-1">
            Free instant search across CarJam records: owner history, WoF, RUC,
            odometer and more.
          </p>

          <div
            className="mt-3 flex flex-col sm:flex-row gap-2"
            data-testid="car-history-check-row"
          >
            <input
              data-testid="car-history-plate-input"
              value={plate}
              onChange={(e) => setPlate(e.target.value.toUpperCase().slice(0, 10))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (!mut.isPending) mut.mutate();
                }
              }}
              placeholder="e.g. ABC123"
              className="input flex-1 uppercase tracking-widest font-mono font-bold"
              maxLength={10}
              autoCapitalize="characters"
              autoComplete="off"
              aria-label="Vehicle registration plate"
            />
            <button
              type="button"
              onClick={() => {
                if (mut.isPending) return;
                mut.mutate();
              }}
              disabled={mut.isPending}
              data-testid="car-history-check-btn"
              className="rounded-full bg-[color:var(--brand-blue)] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-6 py-2.5 text-sm inline-flex items-center justify-center gap-2 transition-colors"
            >
              {mut.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Opening…
                </>
              ) : (
                <>
                  Check on CarJam <ExternalLink className="w-4 h-4" />
                </>
              )}
            </button>
          </div>

          {fallbackUrl && (
            // eslint-disable-next-line react/jsx-no-target-blank -- CarJam affiliate REQUIRES referrer for attribution (20% share). Never noreferrer.
            <a
              href={fallbackUrl}
              target="_blank"
              rel="noopener"
              data-testid="car-history-fallback-link"
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[color:var(--brand-blue)] hover:underline"
            >
              <ExternalLink className="w-3 h-3" /> If the tab didn&apos;t open, click here
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
